import type { GroupLifeQuoteDetails } from '@/services/group-life';
import type { PmeDocusignSignaturesResponse } from '@/services/proposals/types';
import { useBrokerContextSafe } from '@/app/context';
import { useSelectedBrokerWriteGuard } from '@/features/brokers/hooks';
import { toaster } from '@/shared/ui/toaster.component';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useGenerateGroupLifeCommercialLetter } from '../group-life/use-generate-group-life-commercial-letter.hook';
import { useGenerateGroupLifeProposalPdf } from '../group-life/use-generate-group-life-proposal-pdf.hook';
import {
  pmeDocusignSignaturesQueryKey,
  useInitiatePmeDocusignSignatures,
  usePmeDocusignSignaturesStatus,
  useRetryPmeDocusignSignature,
  useSyncPmeDocusignSignatures,
} from './use-pme-docusign-signatures.hook';
import { buildGroupLifeCommercialLetterPayload } from '../../utils/group-life/build-commercial-letter-payload';
import {
  areAllPmeSignaturesCompleted,
  canRetryDocusignSignature,
  canSyncDocusignSignature,
  isSignatureSubmissionStillRunning,
} from '../../utils/docusign-signature-status.util';

/** Tempo para os envelopes de um envio que passou dos 30s do gateway aparecerem. */
const SIGNATURES_REFETCH_AFTER_PENDING_SUBMIT_MS = 20_000;

type Args = {
  policyId: string;
  proposalNumber?: string;
  details?: GroupLifeQuoteDetails;
  effectiveDate?: string;
  /** Chamado quando o Confirmar encaminhou a proposta para subscrição. */
  onSentToAnalysis?: () => void;
};

export function usePmeProposalResumeViewModel({
  policyId,
  proposalNumber,
  details,
  effectiveDate,
  onSentToAnalysis,
}: Args) {
  const queryClient = useQueryClient();
  const brokerContext = useBrokerContextSafe();
  const selectedBroker = brokerContext?.selectedBroker ?? null;
  const signaturesQuery = usePmeDocusignSignaturesStatus(policyId);
  const initiateSignatures = useInitiatePmeDocusignSignatures(policyId);
  const syncSignatures = useSyncPmeDocusignSignatures(policyId);
  const retrySignature = useRetryPmeDocusignSignature(policyId);
  const generateProposalPdf = useGenerateGroupLifeProposalPdf(policyId);
  const generateCommercialLetter =
    useGenerateGroupLifeCommercialLetter(policyId);
  // Corretora inativa: sem confirmar nem assinar (Api barra com 403).
  const brokerGuard = useSelectedBrokerWriteGuard('proposal', 'other');

  const commercialLetterPayload = useMemo(
    () =>
      buildGroupLifeCommercialLetterPayload({
        proposalNumber,
        details,
        effectiveDate,
        broker: selectedBroker,
      }),
    [proposalNumber, details, effectiveDate, selectedBroker]
  );

  const signatures = signaturesQuery.data ?? null;
  const hasSignaturesStarted = Boolean(signatures?.envelopes?.length);
  const allCompleted = areAllPmeSignaturesCompleted(signatures?.envelopes);

  const proposalEnvelope = useMemo(
    () =>
      signatures?.envelopes.find(
        envelope => envelope.envelopeKind === 'proposal'
      ) ?? null,
    [signatures]
  );

  const commercialLetterEnvelope = useMemo(
    () =>
      signatures?.envelopes.find(
        envelope => envelope.envelopeKind === 'commercial_letter'
      ) ?? null,
    [signatures]
  );

  const canSyncAny = useMemo(
    () =>
      Boolean(
        signatures?.envelopes?.some(envelope =>
          canSyncDocusignSignature(envelope)
        )
      ),
    [signatures]
  );

  const handleGenerateProposalPdf = useCallback(() => {
    // O conteúdo do PDF é montado pelo back-end a partir do policyId.
    generateProposalPdf.mutate(undefined, {
      onSuccess: () =>
        toaster.create({
          type: 'success',
          title: 'PDF gerado',
          description: 'O download da proposta foi iniciado.',
        }),
      onError: err =>
        toaster.create({
          type: 'error',
          title: 'Erro ao gerar PDF',
          description: err instanceof Error ? err.message : 'Tente novamente.',
        }),
    });
  }, [generateProposalPdf]);

  const handleGenerateCommercialLetterPdf = useCallback(() => {
    generateCommercialLetter.mutate(commercialLetterPayload, {
      onSuccess: () =>
        toaster.create({
          type: 'success',
          title: 'Carta comercial gerada',
          description: 'O download da carta comercial foi iniciado.',
        }),
      onError: err =>
        toaster.create({
          type: 'error',
          title: 'Erro ao gerar carta comercial',
          description: err instanceof Error ? err.message : 'Tente novamente.',
        }),
    });
  }, [generateCommercialLetter, commercialLetterPayload]);

  const handleConfirm = useCallback(() => {
    // O PDF da proposta é montado pelo back-end; só a carta comercial ainda
    // envia o mapping (migração pendente).
    initiateSignatures.mutate(
      {
        commercialLetter: commercialLetterPayload,
      },
      {
        // O Confirmar roda a subscrição ANTES de disparar as assinaturas. Com
        // pendência, o backend devolve 200 sem criar envelope algum — não dá
        // para anunciar "assinaturas iniciadas" nesse caso.
        onSuccess: (data: PmeDocusignSignaturesResponse) => {
          // Com pendência, o backend devolve 200 sem criar envelope algum —
          // não dá para anunciar "assinaturas iniciadas". Este é o ÚNICO
          // ponto do fluxo que encaminha a proposta para análise; recalcular
          // ao incluir vidas não encaminha.
          if (data.sentToUnderwritingAnalysis) {
            onSentToAnalysis?.();
            return;
          }
          toaster.create({
            type: 'success',
            title: 'Assinaturas iniciadas',
            description:
              'Os documentos foram enviados para assinatura digital via DocuSign.',
          });
        },
        onError: err => {
          if (isSignatureSubmissionStillRunning(err)) {
            toaster.create({
              type: 'info',
              title: 'Envio em andamento',
              description:
                'Os documentos ainda estão sendo enviados para assinatura. Em instantes eles aparecem aqui.',
            });
            window.setTimeout(
              () =>
                void queryClient.invalidateQueries({
                  queryKey: pmeDocusignSignaturesQueryKey(policyId.trim()),
                }),
              SIGNATURES_REFETCH_AFTER_PENDING_SUBMIT_MS
            );
            return;
          }
          toaster.create({
            type: 'error',
            title: 'Erro ao confirmar',
            description:
              err instanceof Error ? err.message : 'Tente novamente.',
          });
        },
      }
    );
  }, [
    initiateSignatures,
    commercialLetterPayload,
    onSentToAnalysis,
    queryClient,
    policyId,
  ]);

  const handleSyncAll = useCallback(() => {
    syncSignatures.mutate(undefined, {
      onSuccess: (data: PmeDocusignSignaturesResponse) => {
        // Com tudo assinado o sync já transmite a proposta (não há mais botão).
        if (data.transmission?.status === 'transmitted') {
          toaster.create({
            type: 'success',
            title: 'Proposta transmitida',
            description:
              'Todas as assinaturas foram concluídas e a proposta foi transmitida.',
          });
          return;
        }
        if (data.transmission?.status === 'failed') {
          toaster.create({
            type: 'error',
            title: 'Proposta não transmitida',
            description: data.transmission.message ?? 'Tente novamente.',
          });
          return;
        }
        toaster.create({
          type: 'success',
          title: 'Status atualizado',
          description: data.allCompleted
            ? 'Todas as assinaturas foram concluídas.'
            : 'O status das assinaturas foi atualizado.',
        });
      },
      onError: err =>
        toaster.create({
          type: 'error',
          title: 'Erro ao atualizar status',
          description: err instanceof Error ? err.message : 'Tente novamente.',
        }),
    });
  }, [syncSignatures]);

  const handleRetry = useCallback(
    (envelopeKind: 'proposal' | 'commercial_letter') => {
      retrySignature.mutate(envelopeKind, {
        onSuccess: () =>
          toaster.create({
            type: 'success',
            title: 'Retentativa enviada',
            description: 'A assinatura digital foi reconciliada.',
          }),
        onError: err =>
          toaster.create({
            type: 'error',
            title: 'Erro ao retentar',
            description:
              err instanceof Error ? err.message : 'Tente novamente.',
          }),
      });
    },
    [retrySignature]
  );

  return {
    signatures,
    hasSignaturesStarted,
    allCompleted,
    proposalEnvelope,
    commercialLetterEnvelope,
    isLoadingSignatures: signaturesQuery.isLoading,
    isGenerateProposalPdfPending: generateProposalPdf.isPending,
    isGenerateCommercialLetterPdfPending: generateCommercialLetter.isPending,
    isConfirmPending: initiateSignatures.isPending,
    isSyncPending: syncSignatures.isPending,
    isRetryPending: retrySignature.isPending,
    canSyncAny,
    canRetryProposal: canRetryDocusignSignature(proposalEnvelope),
    canRetryCommercialLetter: canRetryDocusignSignature(
      commercialLetterEnvelope
    ),
    handleGenerateProposalPdf,
    handleGenerateCommercialLetterPdf,
    handleConfirm,
    handleSyncAll,
    handleRetry,
    isBrokerActionBlocked: brokerGuard.blocked,
    brokerActionBlockedReason: brokerGuard.reason,
  };
}
